import { useEditor, Element } from "@craftjs/core";
import { getRandomId as getRandomNodeId, ROOT_NODE } from "@craftjs/utils";

import React, {
  useContext,
  useCallback,
  useState,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { fromEntries, isJsonString, remove_prefix } from "../../helpers";
import { GrigoraEditorContext } from "../context";
import {
  blockCategories,
  blockCategoryMapping,
  blocksLibrary,
} from "../../blocks";
import { Panel } from "../../input";
import "./toolbox.scss";
import _ from "lodash";
import { waitForElement } from "../../dom";
import { GrigoraDashboardContext } from "../../dashboard/context";
import { addNodeTree, getCloneTree } from "../component-blocks";
import { Component } from "../../../assets/ui-icons";
import { APIProviderContext } from "../../api";
import { PulseLoader } from "react-spinners";

const SectionCard = ({ section }) => {
  const {
    connectors,
    query,
    query: { node, parseFreshNode },
    actions,
    actions: { add, selectNode },
  } = useEditor((state) => ({}));

  const { isAddingSections, setIsAddingSections } =
    useContext(GrigoraEditorContext);

  // add templates where you want
  const handleAdd = useCallback(() => {
    // get the template from your database
    if (!isJsonString(section.data)) {
      return <></>;
    }
    const data = JSON.parse(section.data);
    const newNodes = JSON.parse(data.nodes);
    const nodePairs = Object.keys(newNodes).map((id) => {
      let nodeId = id;

      return [
        nodeId,
        query
          .parseSerializedNode(newNodes[id])
          .toNode((node) => (node.id = nodeId)),
      ];
    });
    var tree = { rootNodeId: data.rootNodeId, nodes: fromEntries(nodePairs) };

    // Clone IDs
    tree = getCloneTree(tree, query);

    return tree;
  }, [section.data]);

  return (
    <div
      ref={
        isJsonString(section.data)
          ? (ref) =>
              connectors.create(ref, handleAdd, {
                onCreate: (nodeTree) => {
                  selectNode(nodeTree.rootNodeId);
                },
              })
          : null
      }
      title={section.section_name}
      onClick={(e) => {
        var tree = handleAdd();

        var selected = query.getEvent("selected");

        var node_parent = ROOT_NODE;
        var node_index = undefined;

        const selectedNodeID = selected.first() ?? ROOT_NODE;
        if (selectedNodeID) {
          if (node(selectedNodeID).isCanvas()) {
            node_parent = selectedNodeID;
            node_index = node(selectedNodeID).childNodes().length;
          } else {
            const parents = node(selectedNodeID).ancestors();

            if (parents && parents.length > 0) {
              node_parent = parents[0];
            }
            if (node_parent) {
              let parentChildrens = node(node_parent).childNodes();
              node_index = parentChildrens.indexOf(selectedNodeID) + 1;
            }
          }
          // Check if addition is allowed in selected nodes
          if (
            "grigora/container" &&
            blocksLibrary[node(node_parent).get()?.data?.custom?.slug] &&
            blocksLibrary[node(node_parent).get()?.data?.custom?.slug]?.craft
              ?.rules?.canMoveIn &&
            !blocksLibrary[
              node(node_parent).get()?.data?.custom?.slug
            ].craft.rules.canMoveIn([tree.nodes[tree.rootNodeId]])
          ) {
            return;
          }
          actions.addNodeTree(tree, node_parent, node_index);
          actions.selectNode(tree.rootNodeId);
          setIsAddingSections(false);
        }
      }}
      onDragStart={(e) => {
        e.target.style.display = "flex";
        document.querySelector(".helper-container").style.pointerEvents =
          "auto";
      }}
      onDragEnd={(e) => {
        setIsAddingSections(false);
      }}
      className="block-card hover:bg-gray-200 flex w-[100%] p-[10px] gap-3 bg-editor rounded-[8px] items-center duration-200 cursor-grab "
    >
      <img
        src={section.section_thumbnail}
        className="w-full object-cover shadow-lg rounded-[5px]"
      />
    </div>
  );
};

const SectionCategories = ({ value, onChange }) => {
  {
    /* Hero, team, location, services, testimonial, features, CTA, counter, coming soon, contact us, newsletter, about us */
  }
  const SECTION_CATEGORIES = [
    {
      name: "hero",
      label: "Hero",
    },
    {
      name: "team",
      label: "Team",
    },
    {
      name: "location",
      label: "Location",
    },
    {
      name: "services",
      label: "Services",
    },
    {
      name: "testimonial",
      label: "Testimonial",
    },
    {
      name: "features",
      label: "Features",
    },
    {
      name: "cta",
      label: "CTA",
    },
    {
      name: "counter",
      label: "Counter",
    },
    {
      name: "coming-soon",
      label: "Coming Soon",
    },
    {
      name: "contact-us",
      label: "Contact Us",
    },
    {
      name: "newsletter",
      label: "Newsletter",
    },
    {
      name: "about-us",
      label: "About Us",
    },
  ];

  return (
    <div className="flex flex-col gap-2">
      {SECTION_CATEGORIES.map((category) => {
        return (
          <div
            key={"section-category-" + category.name}
            onClick={(e) => {
              onChange(category.name);
            }}
            className={`section-category cursor-pointer flex items-center gap-1 rounded-lg py-1 p-2 hover:bg-gray-100 ${
              value === category.name ? "bg-gray-100" : ""
            }`}
          >
            <span className="text-sm font-medium">{category.label}</span>
          </div>
        );
      })}
    </div>
  );
};

const EmptySectionToolbox = ({ children }) => {
  return (
    <div className="flex flex-col flex-1 items-center justify-center">
      {children}
    </div>
  );
};

const SectionToolbox = () => {
  const { getSectionList } = useContext(APIProviderContext);

  const [sections, setSections] = React.useState([]);
  const [isLoadingSections, setIsLoadingSections] = React.useState(true);

  const [activeSectionCategory, setActiveSectionCategory] =
    React.useState("hero");

  useEffect(() => {
    setIsLoadingSections(true);

    // Get sections
    getSectionList(activeSectionCategory)
      .then((res) => {
        setSections(res);
        setIsLoadingSections(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoadingSections(false);
      });
  }, [activeSectionCategory]);

  return (
    <div className="h-full px-[15px] py-[25px] pt-[10px] flex gap-2">
      <div className="w-[135px] pr-[10px] border-r border-r-gray-200">
        <SectionCategories
          value={activeSectionCategory}
          onChange={setActiveSectionCategory}
        />
      </div>
      <div className="flex flex-1 flex-col h-full gap-2 overflow-y-auto grigora-hidden-scrollbar">
        {isLoadingSections ? (
          <EmptySectionToolbox>
            <PulseLoader size={6} />
          </EmptySectionToolbox>
        ) : (
          <>
            {sections && sections.length > 0 && (
              <>
                {sections.map((section) => {
                  return (
                    <>
                      <SectionCard key={section.pk} section={section} />
                    </>
                  );
                })}
              </>
            )}
            {sections && sections.length === 0 && (
              <EmptySectionToolbox>
                <span className="text-gray-400 text-grigora-base">
                  No sections found
                </span>
              </EmptySectionToolbox>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SectionToolbox;
